import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUp, X } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { EXAMPLES, NEEDS, labelForNeed } from "@/features/diagnostic/config";
import { useDiagnostic } from "@/features/diagnostic/DiagnosticContext";
import { Container, Eyebrow } from "./Primitives";
import Reveal from "./Reveal";

const LOOKS = [
  "text-[18px] sm:text-[22px] font-light text-fg/80",
  "text-[16px] sm:text-[19px] font-medium text-fg/70",
  "text-[19px] sm:text-[24px] font-light tracking-[-0.02em] text-fg/75",
  "font-mono text-[13px] sm:text-[15px] uppercase tracking-[0.08em] text-fg/65",
  "text-[17px] sm:text-[21px] font-normal text-fg/80",
  "text-[18px] sm:text-[22px] font-medium tracking-[-0.02em] text-fg/75",
  "text-[19px] sm:text-[24px] font-light text-fg/85",
  "text-[14px] sm:text-[16px] uppercase tracking-[0.12em] text-fg/70",
  "text-[17px] sm:text-[21px] font-normal text-fg/70",
];

const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function useRotatingExample(paused) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (paused || reducedMotion()) return;
    const id = setInterval(() => setI((n) => (n + 1) % EXAMPLES.length), 5000);
    return () => clearInterval(id);
  }, [paused]);
  return EXAMPLES[i];
}

const SelectedNeeds = ({ needs, onRemove }) => (
  <ul aria-label="Selected areas" className="flex flex-wrap gap-2 px-6 pt-5 sm:px-7">
    {needs.map((id) => (
      <li key={id}>
        <button type="button" onClick={() => onRemove(id)} aria-label={`Remove ${labelForNeed(id)}`}
          data-testid={`${HOME.selectedNeed}-${id}`}
          className="fa-btn inline-flex items-center gap-1.5 rounded-full bg-fg/10 px-3 py-1 text-[13px] text-fg hover:bg-fg/20">
          {labelForNeed(id)} <X size={12} aria-hidden />
        </button>
      </li>
    ))}
  </ul>
);

function Composer({ textareaRef, error }) {
  const { state, setDetails, toggleNeed } = useDiagnostic();
  const [focused, setFocused] = useState(false);
  const empty = state.details.length === 0;
  const example = useRotatingExample(focused || !empty);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, [state.details, textareaRef]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      e.currentTarget.form.requestSubmit();
    }
  };

  return (
    <div className="fa-composer rounded-[28px] border border-fg/10 bg-[#1A1A1A] text-left">
      {state.needs.length > 0 && <SelectedNeeds needs={state.needs} onRemove={toggleNeed} />}
      <p id="composer-intro" className="px-6 pt-4 text-[13px] text-subtle sm:px-7">
        Don’t guess. Describe your business problem in your own words. We’ll figure it out.
      </p>
      <label htmlFor="details" className="sr-only">Describe your business problem</label>
      <p id="composer-hint" className="sr-only">Press Control or Command plus Enter to submit.</p>
      <div className="flex items-end gap-3 pb-3 pl-6 pr-3 sm:pl-7">
        <div className="relative min-w-0 flex-1">
          {empty && (
            <p key={example} aria-hidden data-testid={HOME.placeholder}
              className="fa-placeholder pointer-events-none absolute inset-x-0 top-1.5 truncate text-[16px] font-light leading-relaxed text-fg/35 sm:text-[18px]">
              {example}
            </p>
          )}
          <textarea id="details" ref={textareaRef} rows={1} value={state.details} data-testid={HOME.detailsTextarea}
            onChange={(e) => setDetails(e.target.value)} onKeyDown={onKeyDown}
            onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
            aria-describedby="composer-intro composer-hint entry-error" aria-invalid={error || undefined}
            className="block max-h-[240px] min-h-[56px] w-full resize-none overflow-y-auto bg-transparent pb-1 pt-1.5 text-[16px] font-light leading-relaxed text-fg outline-none sm:text-[18px]" />
        </div>
        <button type="submit" aria-label="Analyze my business need" data-testid={HOME.analyzeButton}
          className="fa-btn mb-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-fg text-canvas hover:bg-fg/85">
          <ArrowUp size={19} strokeWidth={2} aria-hidden />
        </button>
      </div>
    </div>
  );
}

function Examples({ textareaRef, onChange }) {
  const { state, setDetails, toggleNeed } = useDiagnostic();
  const [pending, setPending] = useState(null);

  const pick = (need) => {
    onChange();
    const selected = state.needs.includes(need.id);
    toggleNeed(need.id);
    if (selected) return setPending(null);
    if (state.details.trim() && state.details !== need.draft) return setPending(need);
    setDetails(need.draft);
    setPending(null);
    textareaRef.current?.focus();
  };
  const replace = () => { setDetails(pending.draft); setPending(null); textareaRef.current?.focus(); };

  return (
    <div className="mt-8 text-[13px]">
      <p id="examples-label" className="px-2 text-center text-subtle">Try an example:</p>
      <div className="fa-marquee mt-4 py-2">
        <div className="fa-marquee-track">
          <ul className="fa-marquee-group fa-example-group" aria-labelledby="examples-label">
            {NEEDS.map((n, i) => (
              <li key={n.id}>
                <button type="button" aria-pressed={state.needs.includes(n.id)} onClick={() => pick(n)} data-testid={`${HOME.needOption}-${n.id}`}
                  className={`fa-word whitespace-nowrap leading-none underline-offset-[6px] hover:text-fg aria-pressed:text-fg aria-pressed:underline aria-pressed:decoration-fg ${LOOKS[i % LOOKS.length]}`}>
                  {n.label}
                </button>
              </li>
            ))}
          </ul>
          <ul className="fa-marquee-group fa-example-group fa-marquee-dup" aria-hidden="true" inert>
            {NEEDS.map((n, i) => (
              <li key={n.id}><span className={`fa-word inline-block whitespace-nowrap leading-none ${LOOKS[i % LOOKS.length]}`}>{n.label}</span></li>
            ))}
          </ul>
        </div>
      </div>
      {pending && (
        <div role="status" className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-fg/10 px-4 py-3 text-fg">
          <span>Replace your current text with the “{pending.label}” example?</span>
          <button type="button" data-testid={HOME.replaceConfirm} onClick={replace}
            className="fa-btn rounded-full bg-fg px-3 py-1 text-canvas">Replace</button>
          <button type="button" data-testid={HOME.keepMine} onClick={() => setPending(null)}
            className="fa-btn rounded-full border border-fg/20 px-3 py-1 hover:border-fg/50">Keep mine</button>
        </div>
      )}
    </div>
  );
}

export default function AssessmentComposer() {
  const { state, submit, canSubmit } = useDiagnostic();
  const [error, setError] = useState(false);
  const textareaRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { if (canSubmit) setError(false); }, [canSubmit]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) {
      setError(true);
      textareaRef.current?.focus();
      return;
    }
    submit();
    navigate("/assessment/contact");
  };

  return (
    <section id="assessment" data-testid={HOME.entrySection} className="relative bg-canvas pb-40 pt-28 sm:pb-48 sm:pt-36">
      <Container className="flex flex-col items-center text-center">
        <Reveal className="flex flex-col items-center">
          <Eyebrow className="text-tone">AI Assessment</Eyebrow>
          <h2 className="mt-6 max-w-3xl font-display text-4xl font-light leading-[1.04] tracking-[-0.04em] text-fg sm:text-5xl">
            What would you like AI to help with?
          </h2>
          <p className="mt-5 max-w-xl text-base font-light text-subtle md:text-lg">
            Describe what’s slowing your business down. Our experts deliver a working, plug-and-play solution.
          </p>
        </Reveal>
      </Container>
      <Container className="mt-12 max-w-[808px]">
        <Reveal delay={120}>
          <form onSubmit={onSubmit} noValidate>
            <Composer textareaRef={textareaRef} error={error} />
            <p id="entry-error" role="alert" aria-live="polite" data-testid={error ? HOME.entryError : undefined}
              className="min-h-[1.25rem] px-2 pt-3 text-[14px] text-danger">
              {error ? (state.details.length ? "Please describe the problem in words, not just spaces — or pick an example below." : "Describe your business problem or pick an example below to continue.") : ""}
            </p>
            <p className="px-2 text-center text-[12px] text-subtle">Describe the workflow without patient, client, or other sensitive personal details.</p>
            <Examples textareaRef={textareaRef} onChange={() => setError(false)} />
          </form>
        </Reveal>
      </Container>
    </section>
  );
}
